import { ChangeDetectionStrategy, Component, Inject, Input } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { Observable } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import {
    AutocompleteFunction,
    AutocompleteSuggestionFunction,
    DottedKey,
    ExtendedBaseProfileField,
    FieldDefinition,
    FieldReadHelper,
    FormValue,
    FormValueState,
    IconSize,
    isNotNullOrUndefined,
    PartialRecord,
    ShComplexInputFieldComponent,
    ShFieldDefinitionService
} from '@stylehaven/components';
import { uniq } from 'lodash-es';
import isEqual from 'lodash-es/isEqual';
import uniqWith from 'lodash-es/uniqWith';
import { SimpleType } from '../../../../common/entities/common/simple-type.entity';
import { isFormGroup } from '../../../../common/helpers/is-form-group.helper';

@Component({
    standalone: false,
    selector: 'med-tag-input-field',
    templateUrl: './tag-input-field.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TagInputFieldComponent extends ShComplexInputFieldComponent {
    public duplicates: FormValue<SimpleType>[] = [];
    public visibleDuplicates: SimpleType[] = [];

    public suggestions: FormValue<SimpleType>[];
    public visibleSuggestions: SimpleType[];
    public suggestionsEnabled = false;
    private suggestionsControl: UntypedFormControl;

    private readonly suggestionsField$: Observable<FieldDefinition | undefined>;

    public IconSize: typeof IconSize = IconSize;
    private keywordsControl: UntypedFormControl;

    private bulkDescribe: boolean;

    @Input() declare autocompleteFct: AutocompleteFunction;

    constructor(
        protected fieldHelper: FieldReadHelper,
        fieldDefinitionService: ShFieldDefinitionService,
        @Inject('config') config: any
    ) {
        super(fieldHelper);

        this.suggestionsEnabled = config.aiSuggestionsEnabled && !!config.aiSuggestionsField;
        this.suggestionsField$ = fieldDefinitionService.getByDottedKey(config.aiSuggestionsField as DottedKey);
    }

    init(profileField: ExtendedBaseProfileField, fragments: PartialRecord[], control: UntypedFormControl): void {
        this.bulkDescribe = fragments.length >= 2;

        this.suggestionsField$
            .pipe(take(1), takeUntil(this.destroy$), isNotNullOrUndefined())
            .subscribe(suggestionField => this.initializeSuggestions(control, fragments, suggestionField));

        const values: FormValue<SimpleType>[] = this.fieldHelper.getMergedListFieldValue(
            profileField.FieldDefinition,
            fragments
        );

        if (this.bulkDescribe) {
            // Angular Components otherwise emits [] right after this gets called, removing the visible duplicates on bulk describe init
            setTimeout(() => {
                this.detectDuplicatesBetweenFragments(values);
            });
        }

        control.setValue(values);
    }

    private detectDuplicatesBetweenFragments(mapped: FormValue<SimpleType>[]): void {
        const detectedDuplicates: FormValue<SimpleType>[] = uniq(
            mapped.flatMap(current => {
                const mappedWithoutCurrent: FormValue<SimpleType>[] = mapped.filter(
                    mapValue => mapValue.value !== current.value
                );

                // match any case, but do not match with the current value
                return mappedWithoutCurrent.filter(
                    mapValueWithoutCurrent =>
                        new RegExp(mapValueWithoutCurrent.value!.toString(), 'i').test(current.value!.toString()) &&
                        current.value!.toString() !== mapValueWithoutCurrent.value!.toString()
                );
            })
        ).map(detectedDuplicate => new FormValue(detectedDuplicate.value, FormValueState.SET));

        this.duplicates = detectedDuplicates;
        this.updateVisibleDuplicates();
    }

    // #region suggestions
    initializeSuggestions(
        keywordsControl: UntypedFormControl,
        fragments: PartialRecord[],
        suggestionField: FieldDefinition
    ): void {
        this.keywordsControl = keywordsControl;
        const parentControl: UntypedFormGroup | UntypedFormArray | null = this.keywordsControl.parent;

        if (parentControl === null) {
            return;
        }

        if (!isFormGroup(parentControl)) {
            return;
        }

        const mergedSuggestions: FormValue<SimpleType>[] = this.fieldHelper.getMergedListFieldValue(
            suggestionField,
            fragments
        );

        this.suggestionsControl = new UntypedFormControl();
        parentControl.addControl(suggestionField.DottedKey, this.suggestionsControl);

        this.setSuggestions(mergedSuggestions);
    }

    public tagAccepted(tag: string): void {
        this.removeTagFromList(tag);
        this.addKeyword(tag);
    }

    private removeTagFromList(tag: string): void {
        const filteredSuggestion: FormValue<SimpleType>[] = this.suggestions.filter(
            suggestion => suggestion.value !== tag
        );
        this.setSuggestions([...filteredSuggestion, new FormValue(tag, FormValueState.UNSET)]);
    }

    private addKeyword(tag: string): void {
        const currentState: FormValue<SimpleType>[] = this.keywordsControlValue;
        // TODO: ideally the component should use its internal logic to format the input values.
        const unique: FormValue<SimpleType>[] = uniqWith([...currentState, new FormValue(tag)], isEqual);
        this.keywordsControl.setValue(unique);
    }

    public tagDeclined(tag: string): void {
        this.removeTagFromList(tag);
    }

    private setSuggestions(tags: FormValue<SimpleType>[]): void {
        this.suggestions = tags;
        this.suggestionsControl.setValue(tags);
        this.visibleSuggestions = this.suggestions
            .filter(suggestionFormValue => suggestionFormValue.state === FormValueState.SET)
            .map(suggestionFormValue => suggestionFormValue.value);
    }

    // #endregion
    // #region duplicates
    private updateVisibleDuplicates(): void {
        this.visibleDuplicates = uniqWith(this.duplicates, isEqual)
            .filter(duplicateFormValue => duplicateFormValue.state === FormValueState.SET)
            .map(duplicateFormValue => duplicateFormValue.value);
    }

    public receiveDuplicatesEmitted(duplicatesEmitted: FormValue<SimpleType>[]): void {
        this.lastValueEmittedToState(duplicatesEmitted);
        this.updateVisibleDuplicates();
    }

    private lastValueEmittedToState(duplicatesEmitted: FormValue<SimpleType>[]): void {
        this.duplicates.push(...duplicatesEmitted.slice(duplicatesEmitted.length - 1));
    }

    public duplicateAccepted(tag: string): void {
        this.addDuplicate(tag);
        this.removeFromDuplicates(tag);
    }

    private addDuplicate(acceptedDuplicate: string): void {
        const rejectedDuplicates: FormValue<SimpleType>[] = [...this.duplicates, ...this.keywordsControlValue].filter(
            stateDuplicate =>
                new RegExp(acceptedDuplicate, 'i').test(stateDuplicate.value!.toString()) &&
                acceptedDuplicate !== stateDuplicate.value!.toString()
        );

        const rejectedAsFormValuesUnique: FormValue<SimpleType>[] = rejectedDuplicates
            .reduce(
                (
                    uniqueRejectedDuplicatesCollected: FormValue<SimpleType>[],
                    currentRejectedDuplicate: FormValue<SimpleType>
                ): FormValue<SimpleType>[] => [
                    ...uniqueRejectedDuplicatesCollected.filter(
                        collectedRejectedDuplicate =>
                            collectedRejectedDuplicate.value !== currentRejectedDuplicate.value
                    ),
                    currentRejectedDuplicate
                ],
                []
            )
            .map(rejected => new FormValue(rejected.value, FormValueState.UNSET));

        const uniqueState: FormValue<SimpleType>[] = this.keywordsControlValue.filter(
            (currentFormValue: FormValue<SimpleType>) =>
                !rejectedAsFormValuesUnique.some(
                    rejected =>
                        currentFormValue.value === rejected.value || currentFormValue.value === acceptedDuplicate
                ) && currentFormValue.state !== FormValueState.INDETERMINATE
        );

        // because of MERGES Batch and internal handling of case-sensitive duplicate values, order MUST be: MERGE, SUBTRACT
        this.keywordsControl.setValue([
            ...uniqueState,
            new FormValue(acceptedDuplicate),
            ...rejectedAsFormValuesUnique
        ]);

        rejectedDuplicates.forEach(rejectedDuplicate => this.removeFromDuplicates(rejectedDuplicate.value!.toString()));
    }

    private removeFromDuplicates(tag: string): void {
        this.duplicates = this.duplicates.filter(duplicate => duplicate.value !== tag);
        this.updateVisibleDuplicates();
    }

    public duplicateDeclined(tag: string): void {
        this.removeFromDuplicates(tag);
    }

    public get autocompleteFunction(): AutocompleteSuggestionFunction {
        return (term: string) =>
            this.autocompleteFct(term).pipe(
                map(multiFormOptions => multiFormOptions.options.map(option => option.value))
            );
    }

    // #endregion
    public get keywordsControlValue(): FormValue<SimpleType>[] {
        return this.keywordsControl.value;
    }
}
